import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  DEFAULT_SOCKET_FALLBACK_DELAY,
  SOCKET_TRANSPORTS,
  VALUE_CHAIN_AI_AGENT,
} from 'constants/general';
import useWebSocket from 'hooks/useWebSocket';
import { useAppDispatch } from 'store/hooks/useApp';
import valueChainMapApi from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG } from 'store/tags/valueChainSession';
import {
  isVcAgentBomSocketProcessingStatus,
  normalizeAIGeneratingStatus,
} from 'utils/AICreatorHelper';

import type {
  IVCAgentSessionUnitUpdatedPayload,
  IVCAgentSessionUpdatedPayload,
} from 'hooks/interfaces/IUseWebSocket';
import type {
  IUseValueChainAgentSocketsParams,
  IValueChainAgentSocketSessionFilter,
} from '../interfaces/IUseValueChainAgentSockets';

const useValueChainAgentSockets = ({
  valueChainMapId,
  isLaunchRequested,
  sessionFilter,
  socketFallbackDelayRef,
  isAIGeneratingFloatingButtonEnabled,
  refetchVCAgentSessions,
  setLastBomSocketPayload,
  setIsSocketPollingFallbackActive,
  onSupplierResultsDone,
  onSessionReviewPending,
  onMonitoringSessionStartUndone,
}: IUseValueChainAgentSocketsParams) => {
  const dispatch = useAppDispatch();
  const socketNoMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionFilterRef = useRef<IValueChainAgentSocketSessionFilter>(sessionFilter);
  sessionFilterRef.current = sessionFilter;

  const clearSocketNoMessageTimer = useCallback(() => {
    if (!socketNoMessageTimerRef.current) {
      return;
    }

    clearTimeout(socketNoMessageTimerRef.current);
    socketNoMessageTimerRef.current = null;
  }, []);

  // The run history panel reads a session's executions and their runs through this tag. Only the
  // queries of an open panel are subscribed, so only those read again.
  const refreshMonitoringSessionExecutions = useCallback(() => {
    dispatch(valueChainMapApi.util.invalidateTags([VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG]));
  }, [dispatch]);

  const startSocketPollingFallbackTimer = useCallback((delay = DEFAULT_SOCKET_FALLBACK_DELAY) => {
    clearSocketNoMessageTimer();

    socketNoMessageTimerRef.current = setTimeout(() => {
      socketNoMessageTimerRef.current = null;
      setIsSocketPollingFallbackActive(true);
    }, delay);
  }, [clearSocketNoMessageTimer, setIsSocketPollingFallbackActive]);

  const handleBomSocketMessage = useCallback((event: MessageEvent<string>) => {
    try {
      const payload = JSON.parse(event.data) as IVCAgentSessionUnitUpdatedPayload;
      const {
        existingId, bomUnitsId, supplierUnitsId, draftReviewId, monitoringId, monitoringResultsId,
      } = sessionFilterRef.current;

      // A run of the session the results panel shows ended: its row fills in.
      if (payload.sessionId === monitoringResultsId) {
        refreshMonitoringSessionExecutions();
      }

      const isSupplierUnitsSession = payload.sessionId === supplierUnitsId;
      const isRelevantSession = payload.sessionId === existingId
        || payload.sessionId === bomUnitsId
        || payload.sessionId === supplierUnitsId
        || payload.sessionId === draftReviewId
        || payload.sessionId === monitoringId;

      if (!isRelevantSession) {
        return;
      }

      if (payload.message?.trim()) {
        toast.error(payload.message);
      }

      clearSocketNoMessageTimer();

      setLastBomSocketPayload(payload);

      if (!isSupplierUnitsSession || !isVcAgentBomSocketProcessingStatus(payload.status)) {
        refetchVCAgentSessions();
      }
    } catch {
      // Ignore malformed socket payloads.
    }
  }, [
    clearSocketNoMessageTimer,
    refetchVCAgentSessions,
    refreshMonitoringSessionExecutions,
    setLastBomSocketPayload,
  ]);

  const handleSocketPollingFallbackError = useCallback((event: Event) => {
    const errorMessage = event instanceof ErrorEvent ? event.message : '';

    if (!errorMessage.includes('400')) {
      return;
    }

    clearSocketNoMessageTimer();

    setIsSocketPollingFallbackActive(true);
  }, [clearSocketNoMessageTimer, setIsSocketPollingFallbackActive]);

  const handleSocketOpen = useCallback(() => {
    startSocketPollingFallbackTimer(socketFallbackDelayRef.current);
  }, [socketFallbackDelayRef, startSocketPollingFallbackTimer]);

  const shouldConnect = Boolean(valueChainMapId) && isLaunchRequested;

  const { isConnected: isBomSocketConnected, sendVCAgentSessionUnitUpdated: sendBomSocketUnitUpdated } = useWebSocket({
    event: VALUE_CHAIN_AI_AGENT.WS_EVENT_AGENT_SESSION_UNIT_UPDATED,
    transports: [SOCKET_TRANSPORTS.WEBSOCKET],
    shouldConnect,
    onOpen: handleSocketOpen,
    onMessage: handleBomSocketMessage,
    onError: handleSocketPollingFallbackError,
  });

  const handleSessionUpdatedSocketMessage = useCallback((event: MessageEvent<string>) => {
    try {
      const payload = JSON.parse(event.data) as IVCAgentSessionUpdatedPayload;
      const {
        existingId, bomUnitsId, supplierUnitsId, draftReviewId, monitoringId, monitoringResultsId,
      } = sessionFilterRef.current;
      const isRelevantSession = payload.sessionId === existingId
        || payload.sessionId === bomUnitsId
        || payload.sessionId === supplierUnitsId
        || payload.sessionId === draftReviewId
        || payload.sessionId === monitoringId;
      // A monitoring start the backend undid: no run could be sent, and the session is back in
      // unit selection. It can be a session the tab does not follow — one the chat started.
      const isStartUndone = payload.status === VALUE_CHAIN_AI_AGENT.STATUS_UNIT_SELECTION;

      if (isStartUndone || payload.sessionId === monitoringResultsId) {
        refreshMonitoringSessionExecutions();
      }

      if (isStartUndone) {
        onMonitoringSessionStartUndone(payload);
      }

      if (!isRelevantSession) {
        if (isStartUndone) {
          refetchVCAgentSessions();
        }

        return;
      }

      const normalizedSocketStatus = normalizeAIGeneratingStatus(payload.status);

      if (
        isAIGeneratingFloatingButtonEnabled
        && normalizedSocketStatus === VALUE_CHAIN_AI_AGENT.STATUS_DONE
      ) {
        onSupplierResultsDone();
      }

      clearSocketNoMessageTimer();

      onSessionReviewPending(payload.sessionId);
      refetchVCAgentSessions();
    } catch {
      // Ignore malformed socket payloads.
    }
  }, [
    clearSocketNoMessageTimer,
    isAIGeneratingFloatingButtonEnabled,
    onMonitoringSessionStartUndone,
    onSessionReviewPending,
    onSupplierResultsDone,
    refetchVCAgentSessions,
    refreshMonitoringSessionExecutions,
  ]);

  useWebSocket({
    event: VALUE_CHAIN_AI_AGENT.WS_EVENT_AGENT_SESSION_UPDATED,
    transports: [SOCKET_TRANSPORTS.WEBSOCKET],
    shouldConnect,
    onMessage: handleSessionUpdatedSocketMessage,
    onError: handleSocketPollingFallbackError,
  });

  return {
    isBomSocketConnected,
    sendBomSocketUnitUpdated,
    clearSocketNoMessageTimer,
    startSocketPollingFallbackTimer,
  };
};

export default useValueChainAgentSockets;
